import { useId, useState } from 'react';

export interface ChartPoint {
  label: string;
  value: number;
  detail?: string;
}

interface AreaChartProps {
  points: ChartPoint[];
  height?: number;
  seriesName?: string;
}

const WIDTH = 600;

function smoothPath(coordinates: Array<{ x: number; y: number }>): string {
  if (coordinates.length === 0) return '';
  return coordinates.reduce((path, point, index, all) => {
    if (index === 0) return `M ${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
    const previous = all[index - 1]!;
    const controlX = (previous.x + point.x) / 2;
    return `${path} C ${controlX.toFixed(2)} ${previous.y.toFixed(2)}, ${controlX.toFixed(2)} ${point.y.toFixed(2)}, ${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
  }, '');
}

export function AreaChart({ points, height = 158, seriesName = 'Cajas' }: AreaChartProps) {
  const gradientId = useId().replaceAll(':', '');
  const [active, setActive] = useState<number | null>(null);
  const padding = { top: 14, right: 8, bottom: 28, left: 8 };
  const innerHeight = height - padding.top - padding.bottom;
  const values = points.map((point) => point.value);
  const max = Math.max(...values, 1);
  const step =
    points.length > 1 ? (WIDTH - padding.left - padding.right) / (points.length - 1) : 0;

  const coordinates = points.map((point, index) => ({
    x: padding.left + index * step,
    y: padding.top + innerHeight - (point.value / max) * innerHeight,
  }));

  const line = smoothPath(coordinates);
  const area =
    coordinates.length > 0
      ? `${line} L ${coordinates[coordinates.length - 1]!.x.toFixed(2)} ${(padding.top + innerHeight).toFixed(2)} L ${coordinates[0]!.x.toFixed(2)} ${(padding.top + innerHeight).toFixed(2)} Z`
      : '';

  const activePoint =
    active !== null ? points[active] : points.length > 0 ? points[points.length - 1] : undefined;

  const handleMove = (clientX: number, target: SVGSVGElement) => {
    const rect = target.getBoundingClientRect();
    if (rect.width === 0) return;
    const ratio = (clientX - rect.left) / rect.width;
    const index = Math.round(ratio * (points.length - 1));
    setActive(Math.min(points.length - 1, Math.max(0, index)));
  };

  return (
    <div className="area-chart">
      <div className="area-chart__readout">
        <span>{seriesName}</span>
        {activePoint ? (
          <strong>
            {activePoint.value.toLocaleString('es-VE')}{' '}
            <small>{activePoint.detail ?? activePoint.label}</small>
          </strong>
        ) : null}
      </div>
      <svg
        viewBox={`0 0 ${WIDTH} ${height}`}
        role="img"
        aria-label={`Evolución de ${seriesName} en los últimos ${points.length} días`}
        onMouseMove={(event) => handleMove(event.clientX, event.currentTarget)}
        onMouseLeave={() => setActive(null)}
      >
        <title>Evolución de {seriesName}</title>
        <desc>
          {points
            .map((point) => `${point.label}: ${point.value} ${seriesName.toLowerCase()}`)
            .join('. ')}
        </desc>
        <defs>
          <linearGradient id={`area-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--chart-line)" stopOpacity="0.3" />
            <stop offset="100%" stopColor="var(--chart-line)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {[0, 0.33, 0.66, 1].map((ratio) => (
          <line
            key={ratio}
            x1={padding.left}
            x2={WIDTH - padding.right}
            y1={padding.top + innerHeight * ratio}
            y2={padding.top + innerHeight * ratio}
            stroke="var(--chart-grid)"
            strokeDasharray="3 6"
          />
        ))}

        {area ? <path d={area} fill={`url(#area-${gradientId})`} /> : null}
        {line ? (
          <path
            d={line}
            fill="none"
            stroke="var(--chart-line)"
            strokeWidth={2.6}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : null}

        {coordinates.map((point, index) => (
          <g key={points[index]?.label ?? index}>
            {active === index ? (
              <circle cx={point.x} cy={point.y} r={6} fill="var(--chart-line)" fillOpacity={0.16} />
            ) : null}
            <circle
              cx={point.x}
              cy={point.y}
              r={active === index ? 3.4 : 2.4}
              fill="var(--chart-point)"
              stroke="var(--chart-line)"
              strokeWidth={2}
            />
            <text
              x={point.x}
              y={height - 8}
              textAnchor={index === 0 ? 'start' : index === points.length - 1 ? 'end' : 'middle'}
              className="area-chart__label"
            >
              {points[index]?.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}
